import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LockKey, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/admin/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.access_token, data.user);
      toast.success("Welcome back");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-background px-5">
      <div className="pointer-events-none absolute inset-0 radial-fade" />
      <div className="pointer-events-none absolute inset-0 dotgrid opacity-30" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-3xl border border-white/10 bg-card p-8 shadow-2xl">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <LockKey size={26} weight="duotone" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-white">Admin sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Access your FlowCore enquiries dashboard.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
              <Input id="email" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-background" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs text-muted-foreground">Password</Label>
              <Input id="password" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-background" required />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={17} weight="bold" /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
